// Runtime performance tuning — the "prod knobs" half of the build-tuning story
// (see BUILD.md "Build tuning"). TUNING holds per-knob overrides; every consumer
// keeps its existing per-tier expression as the fallback
// (`TUNING.shadowSize ?? (LOW ? 1024 : …)`), so an empty TUNING is exactly
// today's behavior and absent keys still follow the LOW/high tier split.
//
// Sources, in priority order:
//   1. __CHOCKY_TUNING__ — baked in by vite for player builds from
//      chocky.build.json's "runtime" block. This is what /prod/ ships.
//   2. localStorage 'chocky.buildTuning' with preview:true — the editor Build
//      panel's "Preview on reload" toggle. Never consulted in player builds:
//      the __PLAYER_BUILD__ define constant-folds the branch away.
//   3. {} — no overrides.
//
// Plain ESM with typeof guards on both defines — the no-build page loads this
// file with no build flags at all, and vite.config.js/scripts import it under
// Node (where localStorage doesn't exist either).

export const RUNTIME_SPEC = {
  renderScale:       { min: 0.3, max: 1.0,  step: 0.01 },
  dprCap:            { min: 0.5, max: 3.0,  step: 0.25 },
  shadowSize:        { values: [512, 1024, 2048, 4096] },
  shadowInterval:    { min: 0,   max: 4,    step: 1, int: true },
  cameraFar:         { min: 40,  max: 300,  step: 5, int: true },
  fogDensity:        { min: 0,   max: 0.08, step: 0.001 },
  chunkLoadRadius:   { min: 30,  max: 120,  step: 1, int: true },
  chunkUnloadRadius: { min: 34,  max: 140,  step: 1, int: true },
  cloudCount:        { min: 0,   max: 12,   step: 1, int: true },
  wispCount:         { min: 0,   max: 8,    step: 1, int: true },
  maxAniso:          { values: [1, 2, 4, 8, 16] },
  particleScale:     { min: 0.25, max: 2.0, step: 0.05 },
  wolfMax:           { min: 0,   max: 8,    step: 1, int: true },
  eggMax:            { min: 0,   max: 8,    step: 1, int: true },
};
export const TUNING_KEYS = Object.keys(RUNTIME_SPEC);

// Whitelist + clamp raw override objects (baked config, localStorage preview,
// pasted JSON). Unknown keys and non-finite values are dropped, ranged values
// are clamped, enumerated values snap to the nearest allowed entry, and the
// chunk unload ring is kept outside the load ring so streaming can't thrash.
export function sanitizeRuntimeTuning(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const key of TUNING_KEYS) {
    const spec = RUNTIME_SPEC[key];
    let v = raw[key];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (spec.values) {
      let best = spec.values[0];
      for (const c of spec.values) if (Math.abs(c - v) < Math.abs(best - v)) best = c;
      v = best;
    } else {
      v = Math.min(spec.max, Math.max(spec.min, v));
      if (spec.int) v = Math.round(v);
    }
    out[key] = v;
  }
  if (out.chunkUnloadRadius !== undefined || out.chunkLoadRadius !== undefined) {
    const load = out.chunkLoadRadius ?? 58;   // matches world/chunks.js LOAD_RADIUS
    const unload = out.chunkUnloadRadius ?? 66;
    if (unload < load + 4) out.chunkUnloadRadius = load + 4;
  }
  return out;
}

function loadPreview() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('chocky.buildTuning');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.preview !== true) return null;
    return parsed.runtime || null;
  } catch {
    return null;
  }
}

const IS_PLAYER = typeof __PLAYER_BUILD__ !== 'undefined' && __PLAYER_BUILD__;
const BAKED = typeof __CHOCKY_TUNING__ !== 'undefined' ? __CHOCKY_TUNING__ : null;

export const TUNING = Object.freeze(
  sanitizeRuntimeTuning(IS_PLAYER ? BAKED : (BAKED ?? loadPreview()))
);

// Particle pools opt in via a single multiplier; ≥1 so pooled systems never
// have to handle an empty pool.
export const poolScale = (n) => Math.max(1, Math.round(n * (TUNING.particleScale ?? 1)));

if (TUNING_KEYS.some((k) => TUNING[k] !== undefined)) {
  console.log('[tuning] overrides active:', JSON.stringify(TUNING));
}
